import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SharedModule } from '../../shared/shared.module';
import { AuthServiceService } from '../../core/services/authService/auth-service.service';
import { LoginPayload } from '../../core/models/interfaces/login';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { isValidEmail } from '../../core/utils/validators';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';

@Component({
	selector: 'app-user-registration-login',
	standalone: true,
	imports: [SharedModule, CommonModule, MatDialogModule, HasPermissionDirective],
	templateUrl: './user-registration-login.component.html',
	styleUrls: ['../user-registration-login-with-phone/user-registration-login-with-phone.component.scss']
})
export class UserRegistrationLoginComponent implements OnInit, OnDestroy {
	loginForm!: FormGroup;
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	subscription = new Subscription();
	isPasswordFocused: boolean = false;
	isEmailFocused: boolean = false;
	isLoading: boolean = false;
	redirectUrl: string | null = null;
	PermissionForStudent = PermissionForStudents;
	fcmToken: string = '';

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
		private _authRedirectService: AuthRedirectService,
		private _fireBaseService: FireBaseServiceService,
		private _oneSignalService: OneSignalService
	) {
		this.loginForm = new FormGroup({
			email: new FormControl('', [Validators.required, Validators.email, (control: AbstractControl) => isValidEmail(control.value) ? null : { invalidEmailPattern: true }
			])
		});
	}


	ngOnInit(): void {
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'];
			if (params['email']) {
				this.loginForm.patchValue({ email: params['email'] });
				this.isEmailFocused = true;
			}
		});
		this.redirectUrl = this.route.snapshot.queryParamMap.get('redirectUrl');
		this._authRedirectService.redirectIfLoggedIn(this.redirectUrl);
		this.fcmToken = this._authService.getFcmToken() || '';
	}

	private withTimeout<T>(promise: Promise<T>, ms = 3000): Promise<T | ''> {
		const timeout = new Promise<''>((resolve) => setTimeout(() => { resolve('') }, ms));
		return Promise.race([promise, timeout]);
	}

	async onLogin() {
		if (this.loginForm.valid) {
			this.isLoading = true;

			let fcmToken = localStorage.getItem('fcmToken') || '';
			let playerId = localStorage.getItem('onesignal_player_id') || '';

			if (!fcmToken || !playerId) {
				const [fetchedFcm, fetchedPlayer] = await Promise.all([
					!fcmToken ? this.withTimeout(this._fireBaseService.getFCMToken(), 5000) : Promise.resolve(fcmToken),
					!playerId ? this.withTimeout(this._oneSignalService.getPlayerId(), 5000) : Promise.resolve(playerId),
				]);

				fcmToken = fcmToken || fetchedFcm || '';
				playerId = playerId || fetchedPlayer || '';
			}
			const loginPayload: LoginPayload = {
				email: this.loginForm.value.email,
				fcm_token: fcmToken,
				is_default: true,
				login_source: "web",
				onesignal_player_id: playerId
			};

			this.subscription.add(
				this._authService.postLogin(loginPayload).subscribe({
					next: (response) => {
						this.isLoading = false;

						if (!response.Success && response.Status === 401) {
							this.router.navigate(['/mobile-verify'], { queryParams: { redirectUrl: this.redirectUrl } });
							this._toastr.info(response.Message);
							return;
						}

						if (response.Data?.user_id) {
							const newUserId = response.Data.user_id.toString();
							localStorage.setItem('user_id', response.Data.user_id.toString());
							this._authService.currentUserId.next(newUserId);
							this._authService.currentUserId.next(response.Data.user_id.toString());
						}

						if (response.Data?.phone_email) {
							this._authService.currentUserEmail.next(response.Data.phone_email);
							localStorage.setItem('email', response.Data.phone_email);
						}
						this._toastr.success(response.Message);
						this.router.navigate(['/login-email'], { queryParams: { redirectUrl: this.redirectUrl } });
					},
					error: (error) => {
						this.isLoading = false;
						this._toastr.error('Server error occurred. Please try again.');
					}
				})
			);
		}
	}


	togglePassword(field: 'password' | 'confirmPassword') {
		if (field === 'password') {
			this.showPassword = !this.showPassword;
		} else {
			this.showConfirmPassword = !this.showConfirmPassword;
		}
	}


	loginGoogle() {
		const clientId = environment.googleClientId;
		const redirectUri = environment.baseRedirectUrl;
		const redirectParam = this.redirectUrl ? `&state=${encodeURIComponent(this.redirectUrl)}` : '';
		const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=openid%20profile%20email&prompt=select_account
		${redirectParam}`;
		window.location.href = authUrl;
	}


	onEmailFocus(): void {
		this.isEmailFocused = true;
	}

	onEmailBlur(): void {
		this.isEmailFocused = !!this.loginForm.controls['email'].value;
	}

	getEmailPlaceholder(): string {
		return this.isEmailFocused ? '' : 'Enter Email';
	}

	shouldFloatEmailLabel(): boolean {
		return this.isEmailFocused || !!this.loginForm.controls['email'].value;
	}

	onPasswordFocus(): void {
		this.isPasswordFocused = true;
	}

	onPasswordBlur(): void {
		this.isPasswordFocused = !!this.loginForm.controls['password'].value;
	}

	getPasswordPlaceholder(): string {
		return this.isPasswordFocused ? '' : 'Enter your password';
	}

	shouldFloatPasswordLabel(): boolean {
		return this.isPasswordFocused || !!this.loginForm.controls['password'].value;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
